import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Settings,
  Package,
  ChevronDown,
  ChevronRight,
  Shirt,
  Percent,
  UserPlus,
  Save,
  Loader2,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';

interface AdminSettingsPanelProps {
  currentPath: string;
  onLinkClick?: () => void;
}

const settingsLinks = [
  {
    name: 'Clothing & Accessories',
    href: '/admin/products',
    icon: Shirt,
    description: 'Manage products, inventory, pricing',
  },
  {
    name: 'Coupon Management',
    href: '/admin/coupons',
    icon: Percent,
    description: 'Create and manage discount codes',
  },
  {
    name: 'Ambassador Referrals',
    href: '/admin/ambassadors',
    icon: UserPlus,
    description: 'Manage referral codes and commissions',
  },
];

export const AdminSettingsPanel: React.FC<AdminSettingsPanelProps> = ({
  currentPath,
  onLinkClick,
}) => {
  const [expanded, setExpanded] = useState(currentPath.startsWith('/admin'));
  const [webhookUrl, setWebhookUrl] = useState('');
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [loadingWebhook, setLoadingWebhook] = useState(true);

  useEffect(() => {
    fetchWebhookUrl();
  }, []);

  const fetchWebhookUrl = async () => {
    const { data } = await supabase
      .from('admin_settings')
      .select('value')
      .eq('key', 'google_sheet_webhook_url')
      .maybeSingle();
    setWebhookUrl(data?.value || '');
    setLoadingWebhook(false);
  };

  const saveWebhookUrl = async () => {
    setSavingWebhook(true);
    const { error } = await supabase
      .from('admin_settings')
      .upsert(
        { key: 'google_sheet_webhook_url', value: webhookUrl.trim(), updated_at: new Date().toISOString() },
        { onConflict: 'key' }
      );

    if (error) {
      toast.error('Failed to save webhook URL');
    } else {
      toast.success('Google Sheet webhook URL saved');
    }
    setSavingWebhook(false);
  };

  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 px-3 pt-6">
        Admin
      </p>
      
      {/* Main Admin Link */}
      <Link
        to="/admin"
        onClick={onLinkClick}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
          currentPath === '/admin'
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        )}
      >
        <Package className="w-5 h-5" />
        <span className="font-medium">Admin Panel</span>
      </Link>

      {/* Settings Expandable Section */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-colors",
          currentPath.startsWith('/admin/')
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        )}
      >
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5" />
          <span className="font-medium">Settings</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>

      {/* Settings Sub-links */}
      {expanded && (
        <div className="ml-6 mt-1 space-y-1">
          {settingsLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={onLinkClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm group",
                currentPath === link.href
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <link.icon className="w-4 h-4" />
              <div className="flex flex-col">
                <span>{link.name}</span>
                <span className="text-xs text-muted-foreground/70 group-hover:text-muted-foreground">
                  {link.description}
                </span>
              </div>
            </Link>
          ))}

          {/* Google Sheet Webhook Setting */}
          <div className="px-3 py-3 space-y-2 border-t border-border/50 mt-2 pt-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileSpreadsheet className="w-4 h-4" />
              <span>Google Sheet Sync</span>
            </div>
            {loadingWebhook ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : (
              <div className="space-y-2">
                <Label htmlFor="webhook-url" className="text-xs">Webhook URL</Label>
                <Input
                  id="webhook-url"
                  placeholder="https://script.google.com/macros/s/..."
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className="h-8 text-xs"
                />
                <Button
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={saveWebhookUrl}
                  disabled={savingWebhook}
                >
                  {savingWebhook ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Save className="w-3 h-3 mr-1" />
                  )}
                  Save Webhook
                </Button>
                <p className="text-[10px] text-muted-foreground/60 leading-tight">
                  Referral data syncs automatically when orders are confirmed. Create a Google Apps Script web app to receive the data.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
